import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import FullPageSpinner from '../../FullPageSpinner';
import ErrorMessage from '../../common/ErrorMessage';
import { ServerLeaderboardService } from '../../../services/ServerLeaderboardService';
import { getTenantLocations } from '../../../services/scopedReadsService';
import { useApiError } from '../../../hooks/useApiError';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { config } from '../../../config/environment';
import { computeScore } from '../../../utils/scoreUtils';
import * as Sentry from '@sentry/react';

interface Location {
  id: string;
  name: string;
}

interface ServerShift {
  shift_id: string;
  shift_created: string;
  shift_ended: string | null;
  duration_minutes: number;
  interactions_count: number;
}

interface ServerStats {
  server_id: string;
  server_name: string;
  location_id: string;
  location_name: string;
  total_shifts: number;
  total_interactions: number;
  total_duration_minutes: number;
  initial_greeting_count: number;
  criteria_stats: {
    upsell_count: number;
    mention_count: number;
    describe_count: number;
    drink_promotion_count: number;
  };
  shifts: ServerShift[];
}

const ServerLeaderBoard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedTenant } = useTenant();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [serverStats, setServerStats] = useState<ServerStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [locationsError, setLocationsError] = useState<string | null>(null);
  const [serverStatsError, setServerStatsError] = useState<string | null>(null);
  const { error: apiError, handleError: handleApiError, clearError: clearApiError } = useApiError();

  // Determine location filter based on user role
  const getLocationFilter = useMemo(() => {
    if (config.useDummyData) {
      return undefined;
    }
    
    if (!user?.managementUser) {
      return undefined;
    }
    
    const { role, location_id } = user.managementUser;
    
    if (role === 'Admin') {
      return selectedLocation || undefined;
    } else if (role === 'Corporate') {
      return selectedLocation || undefined;
    } else if (role === 'Manager') {
      return location_id;
    }
    
    return undefined;
  }, [user?.managementUser, selectedLocation]);

  // Determine tenant filter based on user role
  const getTenantFilter = useMemo(() => {
    if (config.useDummyData) {
      return undefined;
    }
    
    if (!user?.managementUser) {
      return undefined;
    }
    
    const { role, tenant_id } = user.managementUser;
    
    if (role === 'Admin') {
      return selectedTenant || undefined;
    } else if (role === 'Corporate') {
      return selectedTenant || tenant_id || undefined;
    } else if (role === 'Manager') {
      return selectedTenant || tenant_id || undefined;
    }

    return undefined;
  }, [user?.managementUser, selectedTenant]);

  // feat-133 Batch-3 (ADR 0003 DP-3a option-b): an Admin with no tenant selected
  // must pick one — the scorecard aggregation is per-tenant (no all-tenants path).
  const isAdminWithoutTenant = useMemo(() => {
    const rawRole = user?.managementUser?.role;
    const role = rawRole
      ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase()
      : null;
    return role === 'Admin' && !selectedTenant;
  }, [user?.managementUser?.role, selectedTenant]);

  // Add new state variables for the shift selection modal
  const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
  const [selectedServerShifts, setSelectedServerShifts] = useState<ServerShift[]>([]);
  const [selectedServerName, setSelectedServerName] = useState<string>('');
  const [selectedServerId, setSelectedServerId] = useState<string>('');

  // Get current week's Monday and Sunday in PST
  const getCurrentWeekDates = () => {
    // Get current date in PST
    const today = new Date().toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' });
    const currentDatePST = new Date(today);

    // Find the Monday of current week
    const dayOfWeek = currentDatePST.getDay(); // 0 = Sunday, 1 = Monday, ...
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const mondayDate = new Date(currentDatePST);
    mondayDate.setDate(currentDatePST.getDate() - daysFromMonday);

    // Find the Sunday of current week
    const sundayDate = new Date(mondayDate);
    sundayDate.setDate(mondayDate.getDate() + 6);

    // Format as YYYY-MM-DD using standard date format
    const formatSqlDate = (date: Date): string => {
      return date.toLocaleDateString('en-CA'); // Canadian locale uses YYYY-MM-DD format
    };

    return {
      start: formatSqlDate(mondayDate),
      end: formatSqlDate(sundayDate)
    };
  };

  const { start: START_DATE, end: END_DATE } = getCurrentWeekDates();

  // Format date for display - simple, no timezone conversion needed
  const formatDateDisplay = (dateString: string): string => {
    const date = parseISO(dateString);
    return format(date, 'EEE, MMM d');
  };

  // Get query parameters
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isAdvanced = queryParams.get('advanced') === 'true';

  // Fixed location IDs from the SQL data
  const FREMONT_LOCATION_ID = '22222222-2222-2222-2222-222222222222';
  const CONCORD_LOCATION_ID = '33333333-3333-3333-3333-333333333333';

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLocationsError(null);
        clearApiError();

        // feat-133 Batch-1: scoped ref-lookup replaces the unscoped
        // execute-select `SELECT id,name FROM location`. Tenant is bound
        // server-side from UserContext (Manager/Corporate pinned to own tenant);
        // Admin selects one via the tenant picker. No tenant → no locations.
        if (!getTenantFilter) {
          setLocations([]);
          return;
        }
        const results = await getTenantLocations(getTenantFilter);
        setLocations(results);

        // Set the first location as default if we don't have a selection yet
        if (results.length > 0 && !selectedLocation) {
          setSelectedLocation(results[0].id);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred while fetching locations';
        setLocationsError(errorMessage);
        handleApiError(err);
        Sentry.captureException(err);
        console.error('Error fetching locations:', err);
      }
    };

    fetchLocations();
  }, [getTenantFilter, selectedLocation, clearApiError, handleApiError]);

  // Fetch server data based on location and current week
  useEffect(() => {
    const fetchServerData = async () => {
      setIsLoading(true);
      try {
        setServerStatsError(null);
        clearApiError();

        // feat-133 Batch-3 (ADR 0003 DP-3a option-b): no all-tenants aggregation —
        // an Admin must select a tenant (bail like ServerScorecard/UpsellDashboard).
        // Non-admins always have a tenant via getTenantFilter.
        if (isAdminWithoutTenant) {
          setServerStats([]);
          return;
        }

        const stats = await ServerLeaderboardService.getServerLeaderboardData(START_DATE, END_DATE, undefined, false, getLocationFilter, false, getTenantFilter);
        setServerStats(stats);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred while fetching server data';
        setServerStatsError(errorMessage);
        handleApiError(err);
        Sentry.captureException(err);
        console.error('Error fetching server data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchServerData();
  }, [START_DATE, END_DATE, getLocationFilter, getTenantFilter, isAdminWithoutTenant, clearApiError, handleApiError]);

  // Format date for compact display (MMM d, h:mm a)
  const formatCompactDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';

    // Ensure the date string is treated as UTC
    const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const date = new Date(utcDateString);

    return format(date, 'MMM d, h:mm a'); // No year, 3 letter month, day, and time
  };

  // Check if a date is on or after the start date
  const isDateOnOrAfterStartDate = (dateString: string | null): boolean => {
    if (!dateString) return false;

    // Parse the start date
    const startDate = new Date(START_DATE);
    startDate.setHours(0, 0, 0, 0); // Set to beginning of day

    // Parse the shift date (ensure UTC handling)
    const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const shiftDate = new Date(utcDateString);

    // Convert to PST/PDT for comparison
    const shiftDatePST = new Date(shiftDate.getTime() - (7 * 60 * 60 * 1000)); // Rough PST adjustment
    const shiftDatePSTDay = new Date(
      shiftDatePST.getFullYear(),
      shiftDatePST.getMonth(),
      shiftDatePST.getDate()
    );

    return shiftDatePSTDay >= startDate;
  };

  // Format duration in hours and minutes
  const formatDuration = (minutes: number): string => {
    if (minutes <= 0) return 'N/A';

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    return `${hours > 0 ? `${hours}h ` : ''}${mins}m`;
  };

  // Format percentage
  const formatPercentage = (value: number, total: number) => {
    if (total === 0) return '0%';
    // Ensure we're using the correct calculation
    const percentage = Math.round((value / total) * 100);
    return `${percentage}%`;
  };

  // Calculate server score as average of all 4 metrics
  const calculateServerScore = (server: ServerStats): number => {
    if (server.initial_greeting_count === 0) return 0;

    const upsellPercent = Math.round((server.criteria_stats.upsell_count / server.initial_greeting_count) * 100);
    const mentionPercent = Math.round((server.criteria_stats.mention_count / server.initial_greeting_count) * 100);
    const describePercent = Math.round((server.criteria_stats.describe_count / server.initial_greeting_count) * 100);
    const drinkPercent = Math.round((server.criteria_stats.drink_promotion_count / server.initial_greeting_count) * 100);

    return computeScore([upsellPercent, mentionPercent, describePercent], drinkPercent);
  };

  // Calculate location average score
  const calculateLocationAverage = (locationId: string): number => {
    const locationServers = serverStats.filter(server => server.location_id === locationId);

    if (locationServers.length === 0) return 0;

    const totalScore = locationServers.reduce((sum, server) => {
      return sum + calculateServerScore(server);
    }, 0);

    return Math.round(totalScore / locationServers.length);
  };

  // Get servers for a specific location
  const getServersForLocation = (locationId: string): ServerStats[] => {
    return [...serverStats]
      .filter(server => server.location_id === locationId && server.total_shifts > 0) // Only include servers with shifts
      .map(server => {
        // Filter shifts to only include those on or after START_DATE
        const filteredShifts = server.shifts.filter(shift =>
          isDateOnOrAfterStartDate(shift.shift_created)
        );

        // Recalculate totals based on filtered shifts
        let filteredTotalInteractions = 0;
        let filteredUpsellCount = 0;
        let filteredMentionCount = 0;
        let filteredDescribeCount = 0;
        let filteredDrinkPromotionCount = 0;

        // Only recalculate if we have filtered out shifts
        if (filteredShifts.length !== server.shifts.length) {
          filteredTotalInteractions = filteredShifts.reduce(
            (sum, shift) => sum + shift.interactions_count, 0
          );

          // We need to recalculate criteria stats for the filtered shifts
          // This is an approximation since we don't have the original interactions
          const originalRatio = server.shifts.length > 0 ? filteredShifts.length / server.shifts.length : 0;

          if (originalRatio > 0) {
            filteredUpsellCount = Math.round(server.criteria_stats.upsell_count * originalRatio);
            filteredMentionCount = Math.round(server.criteria_stats.mention_count * originalRatio);
            filteredDescribeCount = Math.round(server.criteria_stats.describe_count * originalRatio);
            filteredDrinkPromotionCount = Math.round(server.criteria_stats.drink_promotion_count * originalRatio);
          }

          // Return modified server stats with filtered data
          return {
            ...server,
            shifts: filteredShifts,
            total_shifts: filteredShifts.length,
            total_interactions: filteredTotalInteractions,
            criteria_stats: {
              upsell_count: filteredUpsellCount,
              mention_count: filteredMentionCount,
              describe_count: filteredDescribeCount,
              drink_promotion_count: filteredDrinkPromotionCount
            }
          };
        }

        // Return unmodified stats if no shifts were filtered out
        return server;
      })
      .sort((a, b) => {
        const scoreA = calculateServerScore(a);
        const scoreB = calculateServerScore(b);
        return scoreB - scoreA;
      });
  };

  const fremontServers = getServersForLocation(FREMONT_LOCATION_ID);
  const concordServers = getServersForLocation(CONCORD_LOCATION_ID);

  const fremontAverageScore = calculateLocationAverage(FREMONT_LOCATION_ID);
  const concordAverageScore = calculateLocationAverage(CONCORD_LOCATION_ID);

  // Add handlers for the shift selection modal
  const handleRowClick = (serverStat: ServerStats) => {
    if (isAdvanced) {
      setSelectedServerShifts(serverStat.shifts);
      setSelectedServerName(serverStat.server_name);
      setSelectedServerId(serverStat.server_id);
      setIsShiftModalOpen(true);
    }
  };

  const handleShiftSelect = (shiftId: string) => {
    navigate(`/restaurant/shifts/${shiftId}/transcript/${selectedServerId}`);
    setIsShiftModalOpen(false);
  };

  const closeShiftModal = () => {
    setIsShiftModalOpen(false);
    setSelectedServerShifts([]);
    setSelectedServerName('');
    setSelectedServerId('');
  };


  // Table rendering function to avoid code duplication
  const renderServerTable = (servers: ServerStats[], locationName: string, averageScore: number, isAdvancedView: boolean) => (
    <div className="bg-white shadow-md rounded-lg p-4 md:p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">{locationName}</h2>
        <div className="flex items-center">
          <span className="text-sm font-medium mr-2">Location Average:</span>
          <span className={`text-lg font-bold ${averageScore >= 70 ? 'text-green-600' :
            averageScore >= 40 ? 'text-yellow-600' : 'text-red-600'
            }`}>
            {averageScore}%
          </span>
        </div>
      </div>

      {servers.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No servers found for {locationName}.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[30%]">
                  Server
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[70%]">
                  Key Metrics
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {servers.map((serverStat) => {
                const score = calculateServerScore(serverStat);

                return (
                  <tr
                    key={serverStat.server_id}
                    className={isAdvancedView ? "hover:bg-blue-50 cursor-pointer transition-colors duration-150" : ""}
                    onClick={() => isAdvancedView && handleRowClick(serverStat)}
                    title={isAdvancedView ? "Click to view shifts" : ""}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-md font-medium ${isAdvancedView ? "text-blue-600" : "text-gray-900"}`}>
                        {serverStat.server_name}
                        {isAdvancedView && (
                          <span className="ml-1 text-xs text-blue-500">→</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{formatDateDisplay(serverStat.shifts[0]?.shift_created)}</div>
                      {serverStat.total_duration_minutes > 0 && (
                        <div className="text-xs text-gray-500">Duration: {formatDuration(serverStat.total_duration_minutes)}</div>
                      )}
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Greetings:</span>
                        <span className="text-xs font-medium">{serverStat.initial_greeting_count}</span>
                      </div>
                      <div className="mt-1 text-sm font-semibold text-blue-600">
                        Score: {score}%
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-3">
                        {/* Premium Menu - Mini progress bar */}
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-medium">Premium Menu</span>
                            <span className="text-xs font-medium">{formatPercentage(serverStat.criteria_stats.upsell_count, serverStat.initial_greeting_count)}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-blue-600 h-1.5 rounded-full"
                              style={{ width: `${serverStat.initial_greeting_count > 0 ? (serverStat.criteria_stats.upsell_count / serverStat.initial_greeting_count * 100) : 0}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Item Mention - Mini progress bar */}
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-medium">Item Mention</span>
                            <span className="text-xs font-medium">{formatPercentage(serverStat.criteria_stats.mention_count, serverStat.initial_greeting_count)}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-green-500 h-1.5 rounded-full"
                              style={{ width: `${serverStat.initial_greeting_count > 0 ? (serverStat.criteria_stats.mention_count / serverStat.initial_greeting_count * 100) : 0}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Item Description - Mini progress bar */}
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-medium">Item Description</span>
                            <span className="text-xs font-medium">{formatPercentage(serverStat.criteria_stats.describe_count, serverStat.initial_greeting_count)}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-purple-500 h-1.5 rounded-full"
                              style={{ width: `${serverStat.initial_greeting_count > 0 ? (serverStat.criteria_stats.describe_count / serverStat.initial_greeting_count * 100) : 0}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Drink Promotion - Mini progress bar */}
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-medium">Drink Promotions</span>
                            <span className="text-xs font-medium">{formatPercentage(serverStat.criteria_stats.drink_promotion_count, serverStat.initial_greeting_count)}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-yellow-500 h-1.5 rounded-full"
                              style={{ width: `${serverStat.initial_greeting_count > 0 ? (serverStat.criteria_stats.drink_promotion_count / serverStat.initial_greeting_count * 100) : 0}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Handle loading state
  if (isLoading) {
    return <FullPageSpinner />;
  }

  // feat-133 Batch-3 (ADR 0003 DP-3a option-b): the leaderboard is a per-tenant
  // scorecard — an Admin must select a tenant (no all-tenants aggregation).
  if (isAdminWithoutTenant) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <p className="text-center text-gray-500 py-12">
          Select a tenant to view the server leaderboard.
        </p>
      </div>
    );
  }

  // Handle critical errors that prevent the page from loading
  if (apiError && !serverStats.length && !locations.length) {
    return (
      <ErrorMessage
        title="Unable to Load Dashboard"
        message="We're having trouble connecting to the database. This could be due to network issues or server maintenance."
        details={apiError?.details}
        onRetry={() => {
          clearApiError();
          window.location.reload();
        }}
        onGoBack={() => navigate('/')}
      />
    );
  }

  // Handle locations error (show partial data if server stats are available)
  if (locationsError && !locations.length) {
    return (
      <ErrorMessage
        title="Unable to Load Locations"
        message="We couldn't load the location data, but you can still view server performance data below."
        details={locationsError}
        onRetry={() => {
          setLocationsError(null);
          window.location.reload();
        }}
        showGoBack={false}
      />
    );
  }

  // Handle server stats error (show partial data if locations are available)
  if (serverStatsError && !serverStats.length) {
    return (
      <ErrorMessage
        title="Unable to Load Server Data"
        message="We couldn't load the server performance data. This could be due to database connectivity issues."
        details={serverStatsError}
        onRetry={() => {
          setServerStatsError(null);
          window.location.reload();
        }}
        showGoBack={false}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">
        Server Performance Dashboard
      </h1>
      <div className="text-sm text-gray-500 mb-6">
        Week of {formatDateDisplay(START_DATE)} to {formatDateDisplay(END_DATE)} (PST)
      </div>

      {/* Show warning if there are partial errors */}
      {(locationsError || serverStatsError) && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Partial Data Available
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                {locationsError && <p>• Location data unavailable</p>}
                {serverStatsError && <p>• Server performance data unavailable</p>}
                <p className="mt-1">Some features may be limited. Try refreshing the page.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {renderServerTable(fremontServers, 'Fremont', fremontAverageScore, isAdvanced)}
      {renderServerTable(concordServers, 'Concord', concordAverageScore, isAdvanced)}

      {/* Add the shift selection modal */}
      {isShiftModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4" onClick={closeShiftModal}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 md:p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4">
                  Select Shift for {selectedServerName}
                </h3>
                <button onClick={closeShiftModal} className="text-gray-400 hover:text-gray-500">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Date
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Duration
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Interactions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {selectedServerShifts.map(shift => (
                      <tr
                        key={shift.shift_id}
                        className="hover:bg-blue-50 cursor-pointer"
                        onClick={() => handleShiftSelect(shift.shift_id)}
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900">
                            {formatDateDisplay(shift.shift_created)}
                          </div>
                          <div className="text-gray-500">
                            {formatTime(shift.shift_created)} - {formatTime(shift.shift_ended)}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {formatDuration(shift.duration_minutes)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {shift.interactions_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-gray-50 px-4 md:px-6 py-3 flex justify-end">
              <button
                onClick={closeShiftModal}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded transition mr-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Add helper function for formatting time
const formatTime = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return format(date, 'h:mm a');
};

export default ServerLeaderBoard; 